import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { AgentAction, AgentBriefingDto, AgentCase, AgentProposalDto, AgentText, AgentTip } from '@immoaktas/shared';
import { Icon, IconButton } from '../ui';
import { ProposalCard } from './ProposalCard';

/**
 * Steering in the panel (docs/17 §Steuerung), all computed without AI on the server: the case card with its
 * checklist and the proposals that wait for this case, one row of suggestions above the composer (next-best-action
 * chips with "Ich bring dich hin" deep links and questions, then the conversation starters of the page) and
 * "Während du weg warst".
 */
export const useText = () => {
  const { t } = useTranslation();
  return (x: AgentText) => t(x.key, x.params ?? {});
};

export function CaseCard({ c, pending, ready, busy, onAsk, onHide }: { c: AgentCase; pending: AgentProposalDto[]; ready: boolean; busy: boolean; onAsk: (q: string) => void; onHide?: () => void }) {
  const { t } = useTranslation();
  const tx = useText();
  const [why, setWhy] = useState<string | null>(null);
  const done = c.steps.filter((s) => s.done).length;
  const current = c.current >= 0 ? c.steps[c.current] : null;
  return (
    <section className="agent-case" aria-label={t('agent.case.label')}>
      <header className="agent-case-head">
        <span className="agent-case-kicker">{t('agent.case.label')}</span>
        <strong>{tx(c.title)}</strong>
        <span className="agent-case-progress">{current ? t('agent.case.progress', { step: c.current + 1, total: c.steps.length }) : t('agent.case.done')}</span>
        {onHide && <IconButton icon="close" label={t('agent.hideTip')} size="sm" className="agent-x" onClick={onHide} />}
      </header>
      <div className="agent-case-bar" role="progressbar" aria-valuemin={0} aria-valuemax={c.steps.length} aria-valuenow={done} aria-label={tx(c.title)}>
        <span style={{ width: `${Math.round((done / Math.max(1, c.steps.length)) * 100)}%` }} />
      </div>
      <ol className="agent-case-steps">
        {c.steps.map((s, i) => (
          <li key={s.id} className={`${s.done ? 'done' : ''}${i === c.current ? ' current' : ''}${s.critical && !s.done ? ' critical' : ''}${s.later && !s.done ? ' later' : ''}`} aria-current={i === c.current ? 'step' : undefined}>
            <span className="agent-case-dot" aria-hidden>{s.done ? <Icon name="check" size={12} /> : i + 1}</span>
            <span className="agent-case-step">
              <span className="agent-case-step-label">
                {tx(s.label)}
                <span className="sr-only"> ({s.done ? t('agent.case.stepDone') : t('agent.case.stepOpen')})</span>
                {!s.done && (s.critical || s.optional || s.later) && <span className={`agent-case-flag ${s.critical ? 'critical' : ''}`}>{t(s.critical ? 'agent.case.flagCritical' : s.later ? 'agent.case.flagLater' : 'agent.case.flagOptional')}</span>}
              </span>
              {(i === c.current || why === s.id) && <small className="agent-case-why">{tx(s.why)}</small>}
              {i === c.current && (
                <span className="agent-case-actions">
                  <Link className="agent-go" to={s.href}><Icon name="arrowRight" size={14} />{t('agent.bringMe')}</Link>
                  <button type="button" className="agent-qchip" disabled={!ready || busy} onClick={() => onAsk(tx(s.prompt))}>{t('agent.case.help')}</button>
                </span>
              )}
              {i !== c.current && !s.done && why !== s.id && (
                <button type="button" className="link muted small" onClick={() => setWhy(s.id)}>{t('agent.case.why')}</button>
              )}
            </span>
          </li>
        ))}
      </ol>
      {pending.length > 0 && (
        <div className="agent-case-pending">
          <p className="agent-case-pending-title">{t('agent.case.pending', { count: pending.length })}</p>
          {pending.map((p) => <ProposalCard key={p.id} p={p} disabled={busy} />)}
        </div>
      )}
    </section>
  );
}

/**
 * One row above the composer: next steps first, then the starters of the page (only in an empty chat). It scrolls
 * sideways (also with the mouse wheel) instead of growing, so the chat keeps its room; the edges fade where more is.
 */
export function Suggestions({ actions, starters, ready, busy, onAsk }: { actions: AgentAction[]; starters: string | null; ready: boolean; busy: boolean; onAsk: (q: string) => void }) {
  const { t } = useTranslation();
  const tx = useText();
  const row = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ left: false, right: false });
  const measure = () => {
    const el = row.current;
    if (!el) return;
    const left = el.scrollLeft > 2;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setEdge((e) => (e.left === left && e.right === right ? e : { left, right }));
  };
  const shown = actions.length > 0 || !!starters;
  useEffect(measure, [actions.length, starters]);
  useEffect(() => {
    if (!shown || typeof ResizeObserver === 'undefined' || !row.current) return;
    const ro = new ResizeObserver(measure);
    ro.observe(row.current);
    return () => ro.disconnect();
  }, [shown]);
  if (!shown) return null;
  return (
    <div
      ref={row}
      className={`agent-suggest${edge.left ? ' fade-left' : ''}${edge.right ? ' fade-right' : ''}`}
      onScroll={measure}
      onWheel={(e) => {
        const el = row.current;
        if (el && Math.abs(e.deltaY) > Math.abs(e.deltaX) && el.scrollWidth > el.clientWidth) el.scrollLeft += e.deltaY;
      }}
    >
      {actions.length > 0 && (
        <div className="agent-chips" role="group" aria-label={t('agent.nextLabel')}>
          {actions.map((a) =>
            a.kind === 'go' && a.href ? (
              <Link key={a.id} className="agent-go chip" to={a.href}>
                <Icon name="arrowRight" size={14} />
                {t('agent.bringMeTo', { target: tx(a.label) })}
              </Link>
            ) : (
              <button key={a.id} type="button" className="agent-qchip" disabled={!ready || busy} onClick={() => onAsk(tx(a.prompt ?? a.label))}>{tx(a.label)}</button>
            ),
          )}
        </div>
      )}
      {starters && <Starters section={starters} ready={ready} busy={busy} onAsk={onAsk} />}
    </div>
  );
}

/** Conversation starters of the page (three chips). */
export function starterSection(path: string, section: string | null): string {
  const seg = path.split('?')[0]!.split('/').filter(Boolean);
  if (seg[0] === 'einheiten' && seg[1]) return seg[2] === 'mietverhaeltnisse' && seg[3] ? 'mietverhaeltnis' : 'einheit';
  return section ?? 'dashboard';
}
export function Starters({ section, ready, busy, onAsk }: { section: string; ready: boolean; busy: boolean; onAsk: (q: string) => void }) {
  const { t, i18n } = useTranslation();
  const key = i18n.exists(`agent.starter.${section}.a`) ? section : 'dashboard';
  return (
    <div className="agent-chips" role="group" aria-label={t('agent.startersLabel')}>
      {(['a', 'b', 'c'] as const).map((k) => {
        const q = t(`agent.starter.${key}.${k}`);
        return <button key={k} type="button" className="agent-qchip" disabled={!ready || busy} onClick={() => onAsk(q)}>{q}</button>;
      })}
    </div>
  );
}

export function AwayCard({ b, renderTip, onSeen }: { b: AgentBriefingDto; renderTip: (tip: AgentTip) => ReactNode; onSeen: () => void }) {
  const { t } = useTranslation();
  return (
    <section className="agent-away" aria-label={t('agent.away.title')}>
      <header>
        <strong>{t('agent.away.title')}</strong>
        <button type="button" className="link muted" onClick={onSeen}>{t('agent.away.seen')}</button>
      </header>
      <p className="small">{t('agent.away.sub')}</p>
      <div className="agent-tips">{b.items.map((tip) => <div key={tip.id}>{renderTip(tip)}</div>)}</div>
    </section>
  );
}
